/**
 * URL schemes the app is willing to navigate to via `window.location.assign`,
 * `window.open`, or any other top-level redirect. Anything outside this set
 * — most importantly `javascript:`, `vbscript:`, `data:`, `file:`, and
 * scheme-relative inputs — is rejected.
 *
 * Kept intentionally narrow (http/https only). Broaden deliberately if a
 * surface needs `mailto:` / `tel:` handoffs.
 */
const SAFE_URL_SCHEMES: ReadonlySet<string> = new Set(['http:', 'https:']);

/**
 * Predicate: `url` is a string parseable as an absolute URL whose scheme is
 * on the allowlist.
 *
 * Use before navigating to any URL that originated from external or
 * user-controlled data (webhook payloads, jsonb columns, custom field
 * values, etc.). Blocks `javascript:`-style XSS, relative-path surprises,
 * and non-string values that survived loose typing.
 *
 * Acts as a type guard so callers get narrowed `string` access after the
 * check.
 */
export function isUrlSafe(url: unknown): url is string {
  if (typeof url !== 'string' || url.length === 0) return false;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  return SAFE_URL_SCHEMES.has(parsed.protocol);
}
